import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {fetchEmailSendingDetails, fetchEmailSendingEvents} from '../../services/firebase'
import useEmailPages from './useEmailPages'
import {DeliveryCounts, displayValue, formatDate, Loading, Pagination, RequestError, Status, statusLabel} from './EmailSendingComponents'
import './EmailSendingDetailModal.css'

const Fields = ({values}) => (
  <dl className='row mb-0'>
    {values.map(([label, value]) => (
      <React.Fragment key={label}>
        <dt className='col-sm-3 text-muted mb-2'>{label}</dt>
        <dd className='col-sm-9 text-break mb-2'>{displayValue(value)}</dd>
      </React.Fragment>
    ))}
  </dl>
)

const Events = ({sendingId}) => {
  const fetchPage = useCallback(cursor => fetchEmailSendingEvents(sendingId, cursor), [sendingId])
  const pager = useEmailPages(fetchPage)

  return (
    <section aria-labelledby='email-events-title'>
      <h3 id='email-events-title'>Event history</h3>
      <p className='text-muted'>Newest event first · 25 per page</p>
      <RequestError error={pager.error} retry={pager.retry} />
      {pager.loading && <Loading />}
      <div className='table-responsive' aria-busy={pager.loading}>
        <table className='table table-row-dashed align-middle gy-4'>
          <thead><tr className='text-muted'>
            {['Occurred / Received', 'Recipient', 'Event', 'Details'].map(label => <th scope='col' key={label}>{label}</th>)}
          </tr></thead>
          <tbody>
            {pager.page?.records.map(event => (
              <tr key={event.id}>
                <td className='text-nowrap'>
                  <div>{formatDate(event.event_at)}</div>
                  <div className='text-muted'>{formatDate(event.received_at)}</div>
                </td>
                <td className='text-break'>{displayValue(event.recipient)}</td>
                <td>{statusLabel(event.event)}</td>
                <td style={{minWidth: 280}}>
                  <Fields values={[
                    ['Provider ID', event.provider_event_id],
                    ['Severity', event.severity],
                    ['Reason', event.reason],
                    ['Diagnostics', event.delivery_status],
                  ]} />
                </td>
              </tr>
            ))}
            {!pager.loading && !pager.error && pager.page?.records.length === 0 && (
              <tr><td colSpan={4} className='text-center py-5'>No events recorded.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination pager={pager} label='Email events' />
    </section>
  )
}

const Recipients = ({recipients}) => (
  <section className='mb-8' aria-labelledby='email-recipients-title'>
    <h3 id='email-recipients-title'>Recipients</h3>
    <div className='table-responsive'>
      <table className='table table-row-dashed align-middle gy-4'>
        <thead><tr className='text-muted'>
          {['Address', 'Delivery', 'Last event', 'Event count', 'Engagement'].map(label => <th scope='col' key={label}>{label}</th>)}
        </tr></thead>
        <tbody>
          {recipients.map(recipient => (
            <tr key={recipient.id}>
              <td className='text-break'>{displayValue(recipient.address)}</td>
              <td><Status value={recipient.delivery_status} /><div className='text-muted'>{formatDate(recipient.delivery_event_at)}</div></td>
              <td>{statusLabel(recipient.last_event)}<div className='text-muted'>{formatDate(recipient.last_event_at)}</div></td>
              <td>{displayValue(recipient.event_count)}</td>
              <td>
                {Object.entries(recipient.engagement || {}).length === 0 ? '—' :
                  Object.entries(recipient.engagement).map(([event, engagement]) => (
                    <div key={event} className='mb-2'>
                      <div>{statusLabel(event)}: {displayValue(engagement?.count)}</div>
                      <div className='text-muted'>First: {formatDate(engagement?.first_at)}</div>
                      <div className='text-muted'>Last: {formatDate(engagement?.last_at)}</div>
                    </div>
                  ))}
              </td>
            </tr>
          ))}
          {recipients.length === 0 && <tr><td colSpan={5} className='text-center py-5'>No recipient tracking recorded.</td></tr>}
        </tbody>
      </table>
    </div>
  </section>
)

const EmailSendingDetailModal = ({sendingId, onClose}) => {
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const requestId = useRef(0)

  const refresh = useCallback(async () => {
    const currentRequest = ++requestId.current
    setLoading(true)
    setError(null)
    // Unmount event pagination so refreshing details starts a new event session.
    setDetails(null)
    try {
      const result = await fetchEmailSendingDetails(sendingId)
      if(currentRequest === requestId.current) setDetails(result)
    } catch(err) {
      if(currentRequest === requestId.current) setError(err)
    } finally {
      if(currentRequest === requestId.current) setLoading(false)
    }
  }, [sendingId])

  useEffect(() => {
    refresh()
    return () => { requestId.current += 1 }
  }, [refresh])

  const sending = details?.sending

  return (
    <Modal show onHide={onClose} dialogClassName='email-sending-detail-dialog' centered scrollable aria-labelledby='email-sending-title'>
      <Modal.Header closeButton>
        <Modal.Title id='email-sending-title'>Email sending details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <RequestError error={error} retry={refresh} />
        {loading && <Loading />}
        {!loading && !error && !details && <div role='status' className='alert alert-info'>This email sending no longer exists.</div>}
        {sending && (
          <>
            <section className='mb-8' aria-labelledby='email-metadata-title'>
              <h3 id='email-metadata-title' className='text-break'>{displayValue(sending.subject)}</h3>
              <Fields values={[
                ['Sending ID', sending.id],
                ['From', sending.from],
                ['To', sending.to?.join(', ')],
                ['BCC', sending.bcc?.join(', ')],
                ['Language', sending.language],
                ['Domain', sending.domain],
                ['Environment', sending.environment],
                ['Project', sending.project_id],
                ['Channel', sending.channel],
                ['Email type', statusLabel(sending.email_type)],
                ['Mailgun ID', sending.mailgun_id],
                ['Created', formatDate(sending.meta?.created)],
                ['Last updated', formatDate(sending.meta?.updated)],
                ['Submitted', formatDate(sending.submitted_at)],
                ['Recipient count', sending.recipient_count],
              ]} />
              <h4 className='mt-5'>Submission</h4>
              <Status value={sending.submission_status} />
              <p className='text-muted mt-2'>Accepted means Mailgun accepted the submission, not that every recipient received it.</p>
              {sending.submission_error && <Fields values={[
                ['Error code', sending.submission_error.code],
                ['Error time', formatDate(sending.submission_error.at)],
              ]} />}
              <h4 className='mt-5'>Delivery counts</h4>
              <DeliveryCounts counts={sending.delivery_counts} />
              <h4 className='mt-5'>Context</h4>
              {Object.keys(sending.context || {}).length
                ? <Fields values={Object.entries(sending.context)} />
                : <p>—</p>}
            </section>
            <Recipients recipients={details.recipients} />
            <Events sendingId={sendingId} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light-primary' disabled={loading} onClick={refresh}>Refresh details</button>
        <button type='button' className='btn btn-light' onClick={onClose}>Close</button>
      </Modal.Footer>
    </Modal>
  )
}

export default EmailSendingDetailModal
